import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, filter, skip } from 'rxjs';
import { MessageBoxCustomButton, MessageDialogComponent } from 'src/app/angular-core/components/message-dialog/message-dialog.component';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { BasicXpacInfo, DeploymentDeviceStatus, DeploymentStatus, Release, UpgradeData } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { DevicesActionsTypes, getMenderReleasesAction, getUpgradeDevicesAction, getUpgradeStatusAction, installMenderArtifactForDevicesAction } from 'src/app/store/actions/devices.actions';
import { selectUserData } from 'src/app/store/selectors/auth.selectors';
import { selectFirmwareReleases, selectFirmwareUpdateStatus, selectUpgradeDevices } from 'src/app/store/selectors/devices.selectors';
import { Roles } from 'src/app/utils/enums/roles';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { hasRoleUserData } from 'src/app/utils/functions/permission.function';
import { append } from 'src/app/utils/functions/string.functions';
import { IUserData } from 'src/app/utils/interfaces/auth.interfaces';
import { IDeviceDeployments, IGetMenderReleasesState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { getTranslationKeyForValue } from 'src/app/angular-core/utils/functions/translation.functions';

interface FirmwareForm {
  firmware: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'xpac-remote-device-update',
  templateUrl: './remote-device-update.component.html',
  styleUrls: ['./remote-device-update.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RemoteDeviceUpdateComponent implements OnInit {
  private static readonly MAX_ITEMS_PER_REQUEST: number = 25;

  public firmwareReleases: Release[] = [];
  public isLoadingReleases: boolean = true;
  public isLoadingFilteredDevices: boolean = true;
  public filteredUpgradeDevices: BasicXpacInfo[] = [];
  public searchText: string = '';

  public selectFirmwareFormGroup: FormGroup<FirmwareForm> = null;
  public firmwareNames: string[] = [];

  private userData: IUserData = null;
  private firmwareName: string = null;
  private selectedXpacIds: Set<string> = new Set<string>();
  private deployments: IDeviceDeployments[] = [];
  private deploymentRefreshSubject: Subject<string[]> = new Subject<string[]>();

  constructor(private store: Store<IRemoteXPACPortalState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private formBuilder: FormBuilder,
    public dialog: DialogAnimationService,
    private actionsListener$: Actions) { }

  public ngOnInit(): void {
    this.initForms();
    this.initAuthInfo();
  }

  private initAuthInfo(): void {
    this.store.select(selectUserData)
      .pipe(
        filter((value: IUserData | null) => value !== null),
        untilDestroyed(this)
      )
      .subscribe((userData: IUserData) => {
        this.userData = userData;
        // Disable the selection for non-CP users, they only get one option from the backend anyway
        if (!this.hasCrossPointRole()) {
          this.selectFirmwareFormGroup.disable();
        }
        this.initData();
      });
  }

  private initData(): void {
    this.initListeners();
    const filter: IGetMenderReleasesState = {};
    this.store.dispatch(getMenderReleasesAction({ data: filter }));   
  }

  private initListeners(): void {
    this.deploymentRefreshSubject
      .pipe(
        filter(hasValue),
        untilDestroyed(this),
        // Add debounce to prevent spamming these request while people are typing
        debounceTime(1000)
      )
      .subscribe((refreshDeviceIds) => {
        this.store.dispatch(getUpgradeStatusAction({ data: refreshDeviceIds }));
      });

    this.store.select(selectFirmwareReleases)
      .pipe(
        filter(hasValue),
        untilDestroyed(this),
      )
      .subscribe((releases: Release[]) => {
        this.firmwareReleases = releases;
        this.setSelectFirmwareFormGroup();
        this.isLoadingReleases = false;
        this.dispatchDeviceRefresh();
        this.cdr.markForCheck();
      });

    this.store.select(selectUpgradeDevices)
      .pipe(
        // Skip the current store value so we will not display wrong data
        skip(1),
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((xpacs: BasicXpacInfo[]) => {
        this.filteredUpgradeDevices = xpacs;
        this.selectedXpacIds.clear();
        this.isLoadingFilteredDevices = false;
        this.refreshUpdateStatus();
        this.cdr.markForCheck();
      });

    this.store.select(selectFirmwareUpdateStatus)
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((deploymentsPerDevice) => {
        // No need to wait with full page loading until we get the update status
        // which is why we don't track the loading state.
        this.deployments = deploymentsPerDevice;
        this.cdr.markForCheck();
      });

    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.INSTALL_MENDER_ARTIFACT_FOR_DEVICES_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const headerText: string = this.translate.instant("Modules.RemoteDeviceUpdateComponent.StartUpdateSucessfullyHeader");
        const messageText: string = this.translate.instant("Modules.RemoteDeviceUpdateComponent.StartUpdateSucessfullyDevicesMessage");
  
        this.dialog.open(MessageDialogComponent, {
          panelClass: 'messagebox-dialog',
          autoFocus: false,
          data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.OK }
        });
      });
  }

  private refreshUpdateStatus(): void {
    this.deploymentRefreshSubject.next(this.filteredUpgradeDevices.map(d => d.deviceId));
  }

  private initForms(): void {
    this.selectFirmwareFormGroup = this.formBuilder.group({
      firmware: ['???', Validators.required],
    });

    this.selectFirmwareFormGroup.get('firmware').valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((firmwareName: string) => {
        const id: string = this.firmwareReleases.find(fa => fa.name === firmwareName)?.name;
        if (hasValue(id)) {
          this.firmwareName = id;
          this.dispatchDeviceRefresh();
        }
      });
  }

  private setSelectFirmwareFormGroup(): void {
    this.firmwareNames = this.firmwareReleases.map((firmware: Release) => firmware.name);
    if (this.firmwareNames.length > 0) {
      this.selectFirmwareFormGroup.patchValue({
        firmware: this.firmwareNames[0],
      });
    }
  }

  public installFirmware(installToAll: boolean): void {
    const firmwareName: string = this.selectFirmwareFormGroup.getRawValue().firmware;

    if (installToAll) {
      const headerText: string = this.translate.instant("Modules.RemoteDeviceUpdateComponent.StartUpdateHeader");
      const messageText: string = this.translate.instant("Modules.RemoteDeviceUpdateComponent.StartUpdateAllDevicesMessage");

      const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
        panelClass: 'messagebox-dialog',
        autoFocus: false,
        data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          // install to all devices
          const upgradeData: UpgradeData = {
            releaseName: firmwareName,
            deviceIds: [],
          };
          this.store.dispatch(installMenderArtifactForDevicesAction({ data: upgradeData }));
        }
      });
    }
    else {
      const headerText: string = this.translate.instant("Modules.RemoteDeviceUpdateComponent.StartUpdateHeader");
      const messageText: string = this.translate.instant("Modules.RemoteDeviceUpdateComponent.StartUpdateDevicesMessage");

      const dialogRef: MatDialogRef<MessageDialogComponent> = this.dialog.open(MessageDialogComponent, {
        panelClass: 'messagebox-dialog',
        autoFocus: false,
        data: { headerInfo: headerText, message: messageText, buttons: MessageBoxCustomButton.YesNo }
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (confirmed) {
          // install to all selected devices   
          const upgradeData: UpgradeData = {
            releaseName: firmwareName,
            deviceIds: this.filteredUpgradeDevices.filter(d => this.selectedXpacIds.has(d.deviceId)).map(d => d.deviceId),
          };
          this.store.dispatch(installMenderArtifactForDevicesAction({ data: upgradeData }));
        }
      });
    }
  }

  public async onSearchTextChanged(searchText: string): Promise<void> {
    this.searchText = searchText;
    this.filteredUpgradeDevices = [];
    this.selectedXpacIds.clear();

    while (this.isLoadingFilteredDevices) {
      // Wait a little so an previous call can be dispatched first.
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.isLoadingFilteredDevices = true;

    this.dispatchDeviceRefresh();
    this.cdr.markForCheck();
  }

  public getTitleString(xpac: BasicXpacInfo): string {
    let title: string = "";

    if (xpac.storeName !== "") {
      title = xpac.storeName;
    }
    else if (xpac.deviceId !== "") {
      title = xpac.deviceId;
    }
    else {
      // default no name
      title = this.translate.instant("Modules.RemoteMaintenance.NoName");
    }

    return title;
  }

  public getInfoString(xpac: BasicXpacInfo): string {
    let info: string = xpac.deviceId;

    if (xpac.storeAddress.city) {
      info = append(info, xpac.storeAddress.city, ' - ');
      info = append(info, xpac.storeAddress.street, ', ');
      info = append(info, xpac.storeAddress.streetNumber, ' ');
    }

    return info;
  }

  public getInfoStringFirmware(xpac: BasicXpacInfo): string {
    let info: string = xpac.firmware;

    const deployment = this.deployments.find(d => d.deviceId === xpac.deviceId)?.deployment;
    // We only want to show the update status if the update has not completed yet, otherwise every single one will show an update status
    if (deployment && deployment.deviceStatus !== DeploymentDeviceStatus.Success) {
      let status: DeploymentDeviceStatus | DeploymentStatus = deployment.deviceStatus;
      if (status === DeploymentDeviceStatus.Unknown) {
        status = deployment.deploymentStatus;
      }
      
      if (status !== DeploymentStatus.Finished) {
        const updatingTo: string = this.translate.instant('Modules.RemoteDeviceUpdateComponent.UpdatingTo', deployment);
        const step: string = this.translate.instant(`Modules.RemoteDeviceUpdateComponent.UpdateStatus.${getTranslationKeyForValue(status)}`);
        info = `${info} - ${updatingTo} - ${step}`;
      }
    }

    return info;
  }

  public onXpacClicked(xpac: BasicXpacInfo): void {
    if (this.selectedXpacIds.has(xpac.deviceId)) {
      this.selectedXpacIds.delete(xpac.deviceId);
    }
    else {
      this.selectedXpacIds.add(xpac.deviceId);
    }
    this.cdr.markForCheck();
  }

  public isSelected(xpac: BasicXpacInfo): boolean {
    return this.selectedXpacIds.has(xpac.deviceId);
  }

  public hasAnySelected(): boolean {
    return this.filteredUpgradeDevices.some(d => this.selectedXpacIds.has(d.deviceId));
  }

  public hasCrossPointRole(): boolean {
    // Technically you need the Mender_Artifacts_History role to be able to see more than 1 entry, but checking for Cross Point will do
    return (hasRoleUserData(Roles.CrosspointAdmin, this.userData) || hasRoleUserData(Roles.Crosspoint, this.userData));
  }

  private dispatchDeviceRefresh(): void {
    // We could plug this.firmwareName directly into the API request, but it's better to make sure it's not garbage before sending the request
    const release = this.firmwareReleases?.find(f => f.name === this.firmwareName);
    if (!release) {
      return;
    }

    this.store.dispatch(getUpgradeDevicesAction({
      data: {
        releaseVersion: release.name,
        maxItems: RemoteDeviceUpdateComponent.MAX_ITEMS_PER_REQUEST,
        search: this.searchText,
      }
    }));
  }
}